import type {
  ATCPageOptionsRowSection,
  ATCPageOptionsRowSectionSlot
} from '@features/add-to-cart-page/typings/OptionsRowSection';
import { useTranslation } from '@i18n/useTranslation';

type TranslationFunction = Awaited<ReturnType<typeof useTranslation>>['t'];

function getDefaultSlotTitle(
  slot: ATCPageOptionsRowSectionSlot,
  t: TranslationFunction,
  productTypeName: string | undefined
): string {
  if (slot.title) {
    return slot.title;
  }

  if (!slot.slotProducts || slot.slotProducts.length === 0) {
    return productTypeName
      ? t('addToCart.optionsRow.noAccessorySlotTitleWithAccessoryType', {
          accessoryType: productTypeName,
          interpolation: { escapeValue: false }
        })
      : t('addToCart.optionsRow.noAccessorySlotTitle');
  }

  return slot.slotProducts[0].name;
}

function getDefaultSlotDescription(
  slot: ATCPageOptionsRowSectionSlot,
  t: TranslationFunction,
  accessoryType: string | undefined
): string {
  if (slot.description) {
    return slot.description;
  }

  if (
    slot.slotProducts?.length &&
    slot.slotProducts[0].prosAndCons &&
    slot.slotProducts[0].prosAndCons.pros[0]
  ) {
    return slot.slotProducts[0].prosAndCons.pros[0];
  }

  return accessoryType
    ? t('addToCart.optionsRow.noAccessorySlotDescriptionWithAccessoryType', {
        accessoryType,
        interpolation: { escapeValue: false }
      })
    : t('addToCart.optionsRow.noAccessorySlotDescription');
}

function getDefaultTitle(t: TranslationFunction, accessoryType: string | undefined): string {
  return accessoryType
    ? t('addToCart.optionsRow.defaultTitleWithAccessoryType', {
        accessoryType,
        interpolation: { escapeValue: false }
      })
    : t('addToCart.optionsRow.defaultTitle');
}

export default function useDefaultContentToOptionsRowSection(
  section: ATCPageOptionsRowSection
): ATCPageOptionsRowSection {
  const { t } = useTranslation('webshop');

  return {
    ...section,
    title: section.title || getDefaultTitle(t, section.productType?.singularName),
    slots: section.slots.map(slot => {
      return {
        ...slot,
        title: getDefaultSlotTitle(slot, t, section.productType?.singularName),
        description: getDefaultSlotDescription(slot, t, section.productType?.singularName)
      };
    })
  };
}
